//import { MoneyLinkException } from "@/shared/utils/custom-exception";
import {
  ErrorDialogContext,
  errorHandledAsync,
  errorHandledSync,
} from "@/shared/context/error-context";
import { LoadingModalContext } from "@/shared/components/loading/loading-context";
import { usePaymentLoginInfoStates } from "@/features/accounts/payment/scraping/hooks/use-login-info-states";
import { useContext } from "react";
import { useInstitutionsContext } from "@/features/institutions/contexts/institutions-context";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { RegisterPaymentAccountsService } from "@/features/accounts/payment/scraping/services/register-accounts-service";
import { UpdatePaymentLoginInfoService } from "@/features/accounts/payment/scraping/services/update-login-info-service";
import { UpdatePaymentAccountsListService } from "@/features/accounts/payment/scraping/services/update-accounts-list-service";
import { useOneTimePassContext } from "@/features/accounts/payment/scraping/contexts/one-time-pass-context";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";

export interface PaymentResult {
  status: string;
  paymentAccountId: string;
  otpFlg: string;
}

export const usePaymentLoginInfo = () => {
  const {
    // 支店名
    branchName,
    setBranchName,
    // ログインID1
    loginId1,
    setLoginId1,
    // ログインID2
    loginId2,
    setLoginId2,
    // ログインパスワード1
    loginPwd1,
    setLoginPwd1,
    // ログインパスワード2
    loginPwd2,
    setLoginPwd2,

    isActivateConfirmButton,
    setIsActivateConfirmButton,
  } = usePaymentLoginInfoStates();
  const {
    selectedAccountGroup,
    selectedAccount,
  } = useAccountGroupsContext();
  const {
    selectedInstitutionService,
  } = useInstitutionsContext();
  const {
    sessionKey,
    otpInfo,
    setOneTimePassList,
    setSessionKey,
  } = useOneTimePassContext();
  const {
    setOpenHomeModal,
  } = useModalContext();

  const registerPaymentAccountsService = new RegisterPaymentAccountsService();
  const updatePaymentLoginInfoService = new UpdatePaymentLoginInfoService();
  const updatePaymentAccountsListService = new UpdatePaymentAccountsListService();

  // グローバルエラーハンドリング
  const { setError } = useContext(ErrorDialogContext);
  // ローディング用コンテキスト
  const loadingCtx = useContext(LoadingModalContext);

  const onChangeBranchName = (branchName: string) => {
    setBranchName(branchName);
  };

  const onChangeLoginId1 = (loginId1: string) => {
    setLoginId1(loginId1);
  };

  const onChangeLoginId2 = (loginId2: string) => {
    setLoginId2(loginId2);
  };

  const onChangeLoginPwd1 = (loginPwd1: string) => {
    setLoginPwd1(loginPwd1);
  };

  const onChangeLoginPwd2 = (loginPwd2: string) => {
    setLoginPwd2(loginPwd2);
  };

  const changeConfirmButtonState = (() => {
    if (!selectedInstitutionService) {
      setIsActivateConfirmButton(false);
      return;
    }

    const branchNameValid = (() => {
      if (selectedInstitutionService.branchNameInputYn === "1") {
        if (selectedInstitutionService.branchNameInputValidYn === "1") {
          if (branchName === "") {
            return false;
          }
        }
      }
      return true;
    })();

    const loginId1Valid = (() => {
      if (selectedInstitutionService.idInputYn === "1") {
        if (selectedInstitutionService.idInputValidYn === "1") {
          if (loginId1 === "") {
            return false;
          }
        }
      }
      return true;
    })();

    const loginId2Valid = (() => {
      if (selectedInstitutionService.id2InputYn === "1") {
        if (selectedInstitutionService.id2InputValidYn === "1") {
          if (loginId2 === "") {
            return false;
          }
        }
      }
      return true;
    })();

    const loginPwd1Valid = (() => {
      if (selectedInstitutionService.pwdInputYn === "1") {
        if (selectedInstitutionService.pwdInputValidYn === "1") {
          if (loginPwd1 === "") {
            return false;
          }
        }
      }
      return true;
    })();

    const loginPwd2Valid = (() => {
      if (selectedInstitutionService.pwd2InputYn === "1") {
        if (selectedInstitutionService.pwd2InputValidYn === "1") {
          if (loginPwd2 === "") {
            return false;
          }
        }
      }
      return true;
    })();

    setIsActivateConfirmButton(
      branchNameValid &&
      loginId1Valid &&
      loginId2Valid &&
      loginPwd1Valid &&
      loginPwd2Valid
    );
  });

  // 設定ボタン押下
  const onClickConfirm = async (
    processType: "reg" | "upd" | "conv" | "updAcc" | "",
  ): Promise<PaymentResult> => {
    loadingCtx.setIsLoading(true);
    try {
      if (processType !== "updAcc" && !selectedInstitutionService) {
        setError(new MoneyLinkWarning({
          title: "エラー",
          message: "選択中の口座グループが存在しません。",
        }));
        return { status: "0", paymentAccountId: "", otpFlg: "0" };
      }
      let status = "0";
      // let loginInfoSid = "";
      let respaymentAccountId = "";
      let urlEncodeOtpInfoContent = "";
      let otpFlg = "0";
      if (otpInfo !== undefined) {
        // otpInfoをjson形式に加工
        const jsonOtpInfo = `[${otpInfo}]`
        // otpInfoContentをbase64エンコード
        const utf8Bytes = new TextEncoder().encode(jsonOtpInfo);
        const base64OtpInfoContent = btoa(String.fromCharCode(...utf8Bytes));
        // otpInfoContentをurlエンコード
        urlEncodeOtpInfoContent = encodeURIComponent(base64OtpInfoContent);
      }
      if (processType === "reg") {
        // processTypeが'reg'の場合はselectedInstitutionServiceが必ず存在する
        const sis = selectedInstitutionService!;
        const res = await registerPaymentAccountsService.registerPaymentAccounts({
          account_group_id: selectedAccountGroup.accountGroupId,
          industry_code: sis.industryCode,
          company_code: sis.companyCode,
          service_code: sis.serviceCode,
          ...(sis.branchCode !== "" && { branch_code: sis.branchCode }),
          ...(sis.branchNameInputYn === "1" && (sis.branchNameInputValidYn === "1" || branchName.trim() !== "") && { branch_no: branchName }),
          ...(sis.idInputYn === "1" && (sis.idInputValidYn === "1" || loginId1.trim() !== "") && { login_id: encodeURIComponent(loginId1) }),
          ...(sis.pwdInputYn === "1" && (sis.pwdInputValidYn === "1" || loginPwd1.trim() !== "") && { login_pwd: encodeURIComponent(loginPwd1) }),
          ...(sis.id2InputYn === "1" && (sis.id2InputValidYn === "1" || loginId2.trim() !== "") && { login_id2: encodeURIComponent(loginId2) }),
          ...(sis.pwd2InputYn === "1" && (sis.pwd2InputValidYn === "1" || loginPwd2.trim() !== "") && { login_pwd2: encodeURIComponent(loginPwd2) }),
          ...(sessionKey !== undefined && { session_key: sessionKey }),
          ...(otpInfo !== undefined && { otp_info: urlEncodeOtpInfoContent }),
        });
        status = res.status;
        if (res.status === "1"){ // 登録完了
          respaymentAccountId = res.payment_account_id;
        }
        else if (res.status === "0" && res.otp_flg === "1") { // OTP入力要求
          otpFlg = res.otp_flg;
          if (res.session_key === "") {
            // throw new MoneyLinkException("セッションキーが取得できません。");
          }
          setSessionKey(res.session_key);
          const listLength = res.otpinfo_list.length;
          if (listLength === 0) {
            // throw new MoneyLinkException("ワンタイムパスワード情報が取得できません。");
          }
          setOneTimePassList(res.otpinfo_list);
          setOpenHomeModal("Otp")
        }
      }
      else if (processType === "upd") {
        if (!selectedAccount) {
          throw new Error("更新対象の口座情報が存在しません。");
        }
        // processTypeが'upd'の場合もselectedInstitutionServiceが必ず存在する
        const sis = selectedInstitutionService!;
        const res = await updatePaymentLoginInfoService.updatePaymentLoginInfo({
          account_group_id: selectedAccountGroup.accountGroupId,
          payment_account_id: selectedAccount.mlinkAccountId,
          payment_id: selectedAccount.mlinkId,
          ...(sis.branchNameInputYn === "1" && (sis.branchNameInputValidYn === "1" || branchName.trim() !== "") && { branch_no: branchName }),
          ...(sis.idInputYn === "1" && (sis.idInputValidYn === "1" || loginId1.trim() !== "") && { login_id: encodeURIComponent(loginId1) }),
          ...(sis.pwdInputYn === "1" && (sis.pwdInputValidYn === "1" || loginPwd1.trim() !== "") && { login_pwd: encodeURIComponent(loginPwd1) }),
          ...(sis.id2InputYn === "1" && (sis.id2InputValidYn === "1" || loginId2.trim() !== "") && { login_id2: encodeURIComponent(loginId2) }),
          ...(sis.pwd2InputYn === "1" && (sis.pwd2InputValidYn === "1" || loginPwd2.trim() !== "") && { login_pwd2: encodeURIComponent(loginPwd2) }),
          ...(sessionKey !== undefined && { session_key: sessionKey }),
          ...(otpInfo !== undefined && { otp_info: urlEncodeOtpInfoContent }),
        });
        status = res.status;
        if (res.status === "1"){ // 更新完了
          respaymentAccountId = selectedAccount.mlinkAccountId;
        }
        else if (res.status === "0" && res.otp_flg === "1") { // OTP入力要求
          otpFlg = res.otp_flg;
          if (res.session_key === "") {
            setError(new MoneyLinkWarning({
              title: "エラー",
              message: "予期せぬエラーが発生しました。\n時間をおいて再度お試しください。",
            }));
            return { status: "0", paymentAccountId: "", otpFlg: "0" };
          }
          setSessionKey(res.session_key);
          const listLength = res.otpinfo_list.length;
          if (listLength === 0) {
            setError(new MoneyLinkWarning({
              title: "ワンタイムパスワード情報の取得に失敗",
              message: "ワンタイムパスワード情報が取得できませんでした。時間をおいて再度お試しください。",
            }));
            return { status: "0", paymentAccountId: "", otpFlg: "0" };
          }
          setOneTimePassList(res.otpinfo_list);
          setOpenHomeModal("Otp")
        }
      }
      else if (processType === "updAcc") {
        if (!selectedAccount) {
          throw new Error("更新対象の口座情報が存在しません。");
        }
        const res = await updatePaymentAccountsListService.updatePaymentAccountsList({
            account_group_id: selectedAccountGroup.accountGroupId,
            payment_account_id: selectedAccount.mlinkAccountId,
            payment_id: selectedAccount.mlinkId,
            ...(sessionKey !== undefined && { session_key: sessionKey }),
            ...(otpInfo !== undefined && { otp_info: urlEncodeOtpInfoContent }),
        });
        status = res.status;
        if (res.status === "1"){ // 更新完了
          respaymentAccountId = selectedAccount.mlinkAccountId;
        }
        else if (res.status === "0" && res.otp_flg === "1") { // OTP入力要求
          otpFlg = res.otp_flg;
          if (res.session_key === "") {
            setError(new MoneyLinkWarning({
              title: "エラー",
              message: "予期せぬエラーが発生しました。\n時間をおいて再度お試しください。",
            }));
            return { status: "0", paymentAccountId: "", otpFlg: "0" };
          }
          setSessionKey(res.session_key);
          const listLength = res.otpinfo_list.length;
          if (listLength === 0) {
            setError(new MoneyLinkWarning({
              title: "ワンタイムパスワード情報の取得に失敗",
              message: "ワンタイムパスワード情報が取得できませんでした。時間をおいて再度お試しください。",
            }));
            return { status: "0", paymentAccountId: "", otpFlg: "0" };
          }
          setOneTimePassList(res.otpinfo_list);
          setOpenHomeModal("SyncOtp")
        }
      }
      return { status: status, paymentAccountId: respaymentAccountId, otpFlg: otpFlg };
    } catch (error) {
      console.log("onClickConfirm", error);
      throw error;
      // return { status: "0", paymentAccountId: "", otpFlg: "0" };
    } finally {
      loadingCtx.setIsLoading(false);
    }
  };

  const resetStates = () => {
    setBranchName("");
    setLoginId1("");
    setLoginId2("");
    setLoginPwd1("");
    setLoginPwd2("");
  };

  return {
    branchName,
    loginId1,
    loginId2,
    loginPwd1,
    loginPwd2,
    isActivateConfirmButton,
    setIsActivateConfirmButton,
    onChangeBranchName:errorHandledSync(onChangeBranchName, setError),
    onChangeLoginId1:errorHandledSync(onChangeLoginId1, setError),
    onChangeLoginId2:errorHandledSync(onChangeLoginId2, setError),
    onChangeLoginPwd1:errorHandledSync(onChangeLoginPwd1, setError),
    onChangeLoginPwd2:errorHandledSync(onChangeLoginPwd2, setError),
    changeConfirmButtonState:errorHandledSync(changeConfirmButtonState, setError),
    onClickConfirm:errorHandledAsync(onClickConfirm, setError),
    resetStates,
  };
};
